import React, { useCallback, useEffect, useState } from 'react';
import {
  CONSENT_ALL,
  CONSENT_NECESSARY,
  enableAnalytics,
  enableNecessaryOnly,
  getConsentEvidence,
  getStoredConsent,
  openCookiePreferences,
} from '../utils/cookieConsent';

const COOKIE_BANNER_ATTR = 'data-ct-cookie-banner';

function setBannerOpenAttr(open) {
  if (typeof document === 'undefined') return;
  if (open) {
    document.documentElement.setAttribute(COOKIE_BANNER_ATTR, 'open');
  } else {
    document.documentElement.removeAttribute(COOKIE_BANNER_ATTR);
  }
}

/**
 * Compact floating cookie / analytics consent toast + manage preferences.
 * Sits above the pinned site footer so version / Privacy / sponsors stay visible.
 */
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [evidence, setEvidence] = useState(null);

  const refresh = useCallback(() => {
    const choice = getStoredConsent();
    setEvidence(getConsentEvidence());
    const nextVisible = choice == null || managing;
    setVisible(nextVisible);
    setBannerOpenAttr(nextVisible);
  }, [managing]);

  useEffect(() => {
    refresh();
    return () => setBannerOpenAttr(false);
  }, [refresh]);

  useEffect(() => {
    const onOpen = () => {
      setManaging(true);
      setVisible(true);
      setBannerOpenAttr(true);
      setEvidence(getConsentEvidence());
    };
    window.addEventListener('ct-open-cookie-preferences', onOpen);
    return () => window.removeEventListener('ct-open-cookie-preferences', onOpen);
  }, []);

  if (!visible) return null;

  const acceptNecessary = () => {
    enableNecessaryOnly(managing ? 'manage' : 'banner');
    setManaging(false);
    setVisible(false);
    setBannerOpenAttr(false);
    setEvidence(getConsentEvidence());
  };

  const acceptAll = () => {
    enableAnalytics(managing ? 'manage' : 'banner');
    setManaging(false);
    setVisible(false);
    setBannerOpenAttr(false);
    setEvidence(getConsentEvidence());
  };

  return (
    <div
      className="cookie-consent-dock pointer-events-none fixed inset-x-0 z-[80] flex justify-center px-2 sm:px-4"
      role="presentation"
      data-testid="cookie-consent-dock"
    >
      <div
        className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-cyan-500/20 bg-slate-950/90 px-3 py-2.5 text-xs shadow-[0_12px_40px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:px-4 sm:py-3"
        role="dialog"
        aria-label="Cookie and analytics consent"
        data-testid="cookie-consent-banner"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="text-[11px] leading-snug text-slate-300/95 sm:text-xs sm:leading-relaxed">
              {managing ? (
                <>
                  Manage cookies: Necessary keeps the workspace running. Analytics &amp; Ads are
                  optional and load Google tags only if you accept them.
                </>
              ) : (
                <>
                  Necessary cookies keep the workspace running. Optional Analytics &amp; Ads help
                  measure acquisition.
                </>
              )}{' '}
              <a
                href="/privacy"
                className="font-semibold text-cyan-300 underline decoration-cyan-500/40 hover:text-cyan-200"
              >
                Privacy
              </a>
              {' · '}
              <a
                href="/cookies"
                className="text-cyan-300 underline decoration-cyan-500/40 hover:text-cyan-200"
              >
                Cookies
              </a>
            </p>
            {managing && evidence?.ts ? (
              <p className="mt-1 font-mono text-[10px] text-slate-500">
                Last choice: {evidence.choice} @ {evidence.ts} (v{evidence.cookiePolicyVersion})
              </p>
            ) : null}
          </div>
          <div className="flex min-w-0 shrink-0 flex-row flex-wrap gap-2">
            {managing ? (
              <button
                type="button"
                onClick={() => {
                  setManaging(false);
                  if (getStoredConsent() != null) {
                    setVisible(false);
                    setBannerOpenAttr(false);
                  }
                }}
                className="inline-flex h-8 items-center justify-center rounded-lg border border-white/10 px-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 transition hover:text-slate-200 sm:h-7 sm:text-xs"
              >
                Close
              </button>
            ) : null}
            <button
              type="button"
              onClick={acceptNecessary}
              className="inline-flex h-8 min-w-0 flex-1 items-center justify-center rounded-lg border border-white/15 bg-slate-900/60 px-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-200 transition hover:border-white/30 sm:h-7 sm:flex-none sm:text-xs"
              data-testid="cookie-accept-necessary"
            >
              <span className="sm:hidden">Necessary</span>
              <span className="hidden sm:inline">Accept necessary</span>
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="inline-flex h-8 min-w-0 flex-1 items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-500/25 px-2.5 text-[10px] font-bold uppercase tracking-wider text-cyan-50 transition hover:bg-cyan-500/35 sm:h-7 sm:flex-none sm:text-xs"
              data-testid="cookie-accept-all"
            >
              <span className="sm:hidden">Accept all</span>
              <span className="hidden sm:inline">Accept all (Analytics &amp; Ads)</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { CONSENT_ALL, CONSENT_NECESSARY, openCookiePreferences };
